/*@flow*/
import React from 'react';
import Blank from 'lib/Blank';
import isNil from 'lib/t/isNil';
import { type StructType } from 'lib/t/types';
import isString from 'lib/t/isString';
import numberFormat from 'underscore.string/numberFormat';
import dateFnsformat from 'date-fns/format';
import weekdays from 'lib/weekdays';
import * as log from 'lib/log';
import NonEmptyArray from 'lib/NonEmptyArray';
import * as t from 'lib/t';
import compose from 'lib/compose';
import isEmbedded from 'lib/isEmbedded';
import truthy from 'lib/truthy';
import Subresource from './Subresource';
import useLocals from 'lib/proforma/hooks/useLocals';
// import toIsoStringWithOffset from 'lib/toIsoStringWithOffset';

function toLink(value) {
  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      title="Opens in another tab"
    >
      Visit Website
    </a>
  );
}

function mkString(value: string | void | any[]) {
  let v2 = value;
  if (typeof value === 'string') {
    try {
      v2 = JSON.parse(value);
      v2 = v2.join(', ');
    } catch (_) {} //eslint-disable-line
  } else if (Array.isArray(value)) {
    v2 = value.join(', ');
  }
  return v2;
}

const DATE_FORMAT_OPTION = { awareOfUnicodeTokens: true };

function toPrettyDate(value) {
  const tmp = 'MM/dd/yyyy';
  try {
    return dateFnsformat(new Date(value), tmp, DATE_FORMAT_OPTION);
  } catch (e) {
    log.error('INVALID TIME VALUE: ', value);
    return new Date();
  }
}

function toEmail(value) {
  return (
    <a
      target="_blank"
      rel="noopener noreferrer"
      title="Opens in another tab"
      href={`mailto:${value}`}
    >
      {value}
    </a>
  );
}

function toPhone(value) {
  return (
    <a
      tabIndex={0}
      target="_blank"
      href={`tel:${value}`}
      rel="noopener noreferrer"
    >
      {value}
    </a>
  );
}

function toHTML(value) {
  return (
    <div
      style={{ textAlign: 'initial' }}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
}

function toPhoto(value) {
  if (!ENV) return;
  const key = ENV.FILEPICKER_API_KEY;
  const imgSrc = `https://cdn.filestackcontent.com/${key}/resize=width:300/rotate=deg:exif/${value}`;
  return (
    <div className="text-center">
      <img src={imgSrc} />
    </div>
  );
}

// Transforms non-standard time string to ISO-8601.
//
// Before: '2019-09-30 12:00:00 UTC'
// After: '2019-09-30T12:00:00+00:00'
function toISO8601(v) {
  if (isString(v) && v.match('UTC')) {
    v = v.replace(/ UTC$/, '+00:00').replace(/ /, 'T');
  }
  return v;
}

function toTime(v) {
  const tmp = 'MMMM do, YYYY - h:mm a';
  v = toISO8601(v);

  try {
    let d = new Date(v);
    return dateFnsformat(d, tmp, DATE_FORMAT_OPTION);
  } catch (e) {
    log.error(e);
  }
}

function toMoney(value) {
  let v2 = value;
  if (typeof value === 'string') {
    v2 = parseFloat(value);
  }
  return numberFormat(v2, 2);
}

function toNumber(value) {
  let v2 = value;
  if (typeof value === 'string') {
    v2 = parseInt(value, 10);
  }
  return numberFormat(v2, 0);
}

function toNonZeroNumber(value) {
  let v = toNumber(value);
  return v === 0 ? null : v;
}

function toHours(v) {
  if (v.length === 0) return;

  return v.map((d) => {
    const dayName = weekdays[d.day];
    let intervals = 'Open 24 Hours';
    if (NonEmptyArray.is(d.intervals)) {
      intervals = d.intervals.map((int, i) => (
        <p key={i}>
          {int.opening} - {int.closing}
        </p>
      ));
    }

    return (
      <div key={d.day} className="d-flex">
        <div>{dayName}: </div>
        <div className="ml-auto">{intervals}</div>
      </div>
    );
  });
}

function toBool(v) {
  if (isNil(v)) return;
  return truthy(v) ? 'yes' : 'no';
}

function addPrefix(pref) {
  return (value) => `${pref}${value}`;
}

function addSuffix(suff) {
  return (value) => `${value}${suff}`;
}

// Returns nil values as-is without transformations.
function non(fn) {
  return (value) => {
    if (value === '0.00') return null;
    return Blank.is(value) ? value : fn(value);
  };
}

const identity = (v) => v;

function TableRow({ label, value, hideWhen }) {
  if (Blank.is(value)) return null;
  if (hideWhen && hideWhen()) return null;

  if (!label) {
    return <div className="row-without-label">{value}</div>;
  }

  return (
    <div className="table-row">
      <div>{label}</div>
      <div>{value}</div>
    </div>
  );
}

function AddressRow({ label, value, hideWhen }) {
  const locals = useLocals(['latlon']);
  if (Blank.is(value)) return null;
  if (hideWhen && hideWhen()) return null;
  if (!label) {
    return <div className="row-without-label">{value}</div>;
  }

  const latlon = locals.value;
  let url = '#';
  if (latlon) {
    url = `https://maps.google.com/?q=${latlon.join(',')}`;
  }
  const directions = (
    <a href={url} target="_blank" rel="noopener noreferrer">
      Directions
    </a>
  );

  return (
    <div className="table-row">
      <div>{label}</div>
      <div>
        {value} {directions}
      </div>
    </div>
  );
}

const mkRowTWithTemplate = (template) => (...formatters) => {
  const format = non(compose(...formatters));
  return t.Any.opt<{
    template: Object,
    tx: { format: Function, parse: Function },
  }>({
    template,
    tx: { format, parse: identity },
  });
};

const rowT = mkRowTWithTemplate(TableRow);
const rowAddressT = mkRowTWithTemplate(AddressRow);

const StringType: any = rowT(identity);
const AddressType: any = rowAddressT(identity);
const BooleanType: any = rowT(toBool);
const NumberType: any = rowT(toNumber);
const NonZeroNumberType: any = rowT(toNonZeroNumber);
const ArrayType: any = rowT(mkString);
const HiddenType: any = StringType.opt<any>({ hideWhen: () => true });
const UrlType = rowT(toLink);
const DateType: any = rowT(toPrettyDate); // eslint-disable-line
const EmailType: any = rowT(toEmail);
const PhoneType: any = rowT(toPhone);
const HTMLType: any = rowT(toHTML);
const PhotoType: any = rowT(toPhoto).opt<any>({ label: null });
const MoneyType: any = rowT(addPrefix('$'), toMoney);
const TimeType: any = rowT(toTime);
const FeetType = rowT(addSuffix(' FT'), toNumber);
const SqFtType: any = rowT(addSuffix(' SQ FT'), toNumber);
const TagType: any = ArrayType.opt<any>({ hideWhen: isEmbedded });
const NoteType: any = HTMLType.opt<any>({ hideWhen: isEmbedded });

const buildingTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  description: HTMLType,
  category: ArrayType,
  tags: TagType,
  propertyMunicipalId: (StringType.opt<any>({
    label: 'Municipal Property ID',
  }): any),
  bin: (StringType.opt<any>({ label: 'BIN #' }): any),
  propertyManagement: StringType,
  contact: (StringType.opt<any>({ label: 'Contact Name' }): any),
  contactPhone: StringType,
  contactEmail: StringType,
  uses: (ArrayType.opt<any>({ label: 'Use' }): any),
  amenities: ArrayType,
  numStories: (NumberType.opt<any>({ label: 'Number of stories' }): any),
  numUnits: (NumberType.opt<any>({ label: 'Number of units' }): any),
  housingUnitsTotal: (NumberType.opt<any>({
    label: 'Total housing units',
  }): any),
  numAffordableUnits: (NumberType.opt<any>({
    label: 'Number of afforable units',
  }): any),
  numMarketRateUnits: (NumberType.opt<any>({
    label: 'Number of market rate units',
  }): any),
  totalFloorAreaSqft: (SqFtType.opt<any>({
    label: 'Total floor area (sqft)',
  }): any),
  residentialSqFt: (SqFtType.opt<any>({
    label: 'Residential area (sqft)',
  }): any),
  retailSqFt: (SqFtType.opt<any>({ label: 'Retail area (sqft)' }): any),
  officeSqFt: (SqFtType.opt<any>({ label: 'Office area (sqft)' }): any),
  commercialSqFt: (SqFtType.opt<any>({ label: 'Commercial area (sqft)' }): any),
  hotelSqFt: (SqFtType.opt<any>({ label: 'Hotel area (sqft)' }): any),
  industrialSqFt: (SqFtType.opt<any>({ label: 'Industrial area (sqft)' }): any),
  otherSqFt: (SqFtType.opt<any>({ label: 'Other area (sqft)' }): any),
  amenitiesSqFt: (SqFtType.opt<any>({ label: 'Amenities area (sqft)' }): any),
  rentableAreaSqft: (SqFtType.opt<any>({ label: 'Rentable area (sqft)' }): any),
  typicalFloorSizeSqft: (SqFtType.opt<any>({
    label: 'Typical floor size (sqft)',
  }): any),
  totalUnits: (NumberType.opt<any>({ label: 'Total units' }): any),
  yearBuilt: StringType,
  yearLastRenovated: StringType,
  phone: PhoneType,
  url: (UrlType.opt<any>({ label: 'Website' }): any),
  notes: NoteType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const contactTypeConfig = {
  contactType: ArrayType,
  tags: TagType,
  organizationName: StringType,
  mailingAddress: StringType,
  email: EmailType,
  phone: PhoneType,
  cellPhone: PhoneType,
  homePhone: PhoneType,
  fax: PhoneType,
  city: StringType,
  zip: StringType,
  stateOrProvince: StringType,
  title: StringType,
  notes: NoteType,
  propertyAddress: StringType,
  propertyLotId: (StringType.opt<any>({ label: 'Lot Id' }): any),
  propertyCity: (StringType.opt<any>({ label: 'City' }): any),
  propertyCountryAbbr: (StringType.opt<any>({ label: 'Country' }): any),
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const developmentTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  developmentType: StringType,
  description: HTMLType,
  floorArea: NonZeroNumberType,
  financing: StringType,
  expectedCompletionYear: StringType,
  uses: ArrayType,
  tags: TagType,
  notes: NoteType,
  url: (UrlType.opt<any>({ label: 'Website' }): any),
  cost: MoneyType,
  program: StringType,
  multifamilyUnits: NumberType,
  affordableMultifamilyUnits: NumberType,
  multifamilySqft: (SqFtType.opt<any>({ label: 'Multifamily sq ft' }): any),
  officeSqft: (SqFtType.opt<any>({ label: 'Office sq ft' }): any),
  retailSqft: (SqFtType.opt<any>({ label: 'Retail sq ft' }): any),
  commercialSqft: SqFtType,
  industrialSqft: (SqFtType.opt<any>({ label: 'Industrial sq ft' }): any),
  otherSqft: (SqFtType.opt<any>({ label: 'Other sq ft' }): any),
  hospitalityUnits: NumberType,
  hospitalitySqft: SqFtType,
  commFacilitiesSqft: (SqFtType.opt<any>({
    label: 'Community Facilities sq ft',
  }): any),
  parkingSpots: NumberType,
  architect: StringType,
  developer: StringType,
  ownershipType: StringType,
  floors: NumberType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const eventTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  startsAt: TimeType,
  endsAt: TimeType,
  // isRecurring: StringType.opt<any>({ label: 'Repeats' }),
  category: StringType,
  tags: TagType,
  locationName: StringType,
  description: HTMLType,
  url: (UrlType.opt<any>({ label: 'Website' }): any),
  registrationUrl: StringType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const listingTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  listingType: StringType,
  rentableSqft: (SqFtType.opt<any>({ label: 'Available Space' }): any),
  availability: StringType,
  ceilingHeightFeet: (FeetType.opt<any>({ label: 'Ceiling Height' }): any),
  frontageFeet: (FeetType.opt<any>({ label: 'Frontage' }): any),
  contactName: (StringType.opt<any>({ label: 'Contact Name' }): any),
  contactCompany: (StringType.opt<any>({ label: 'Contact Company' }): any),
  contactEmail: EmailType,
  phone: (PhoneType.opt<any>({ label: 'Contact Phone' }): any),
  secondaryContactName: StringType.opt<any>({ hideWhen: isEmbedded }),
  secondaryContactPhone: PhoneType.opt<any>({ hideWhen: isEmbedded }),
  secondaryContactEmail: EmailType.opt<any>({ hideWhen: isEmbedded }),
  description: HTMLType,
  intendedUse: ArrayType,
  currentUses: ArrayType,
  tags: TagType,
  notes: NoteType,
  url: (UrlType.opt<any>({ label: 'Website' }): any),
  email: EmailType,
  incentives: StringType,
  pricePerSqft: (MoneyType.opt<any>({ label: 'Price per square foot' }): any),
  askingRentPerMonth: MoneyType,
  askingPrice: MoneyType,
  marketValue: MoneyType,
  auxSpaceSqft: SqFtType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const propertyTypeConfig = {
  municipalId: (StringType.opt<any>({ label: 'Municipal ID' }): any),
  address: AddressType,
  latlon: HiddenType,
  category: StringType,
  description: StringType,
  nodes: StringType,
  tags: TagType,
  annualPropertyTax: MoneyType,
  annualDistrictAssessment: MoneyType,
  section: NumberType,
  propertyBlockId: (StringType.opt<any>({ label: 'Block' }): any),
  propertyLotId: (StringType.opt<any>({ label: 'Lot' }): any),
  condo: NumberType,
  owner: StringType,
  propertyOwners: StringType,
  propertyClass: ArrayType,
  taxClass: ArrayType,
  districtClass: ArrayType,
  annualDistrictAssessmentExemptions: NumberType,
  easement: BooleanType,
  totalAssessedValue: MoneyType,
  lotPerimeter: (NumberType.opt<any>({ label: 'Perimeter (FT)' }): any),
  lotDepth: (NumberType.opt<any>({ label: 'Depth (FT)' }): any),
  lotFront: (NumberType.opt<any>({ label: 'Front (FT)' }): any),
  lotSide1: (NumberType.opt<any>({ label: 'Side 1 (FT)' }): any),
  lotSide2: (NumberType.opt<any>({ label: 'Side 2 (FT)' }): any),
  lotSide3: (NumberType.opt<any>({ label: 'Side 3 (FT)' }): any),
  lotRear: (NumberType.opt<any>({ label: 'Rear (FT)' }): any),
  lotAreaTotal: (NumberType.opt<any>({ label: 'Lot Area Total (SQ FT)' }): any),
  residentialArea: NumberType,
  commercialArea: (NumberType.opt<any>({ label: 'Commercial (SQ FT)' }): any),
  retailArea: (NumberType.opt<any>({ label: 'Retail (SQ FT)' }): any),
  officeArea: (NumberType.opt<any>({ label: 'Office (SQ FT)' }): any),
  industrialArea: (NumberType.opt<any>({ label: 'Industrial (SQ FT)' }): any),
  agriculturalArea: (NumberType.opt<any>({
    label: 'Agricultural (SQ FT)',
  }): any),
  communityArea: (NumberType.opt<any>({ label: 'Community (SQ FT)' }): any),
  otherArea: (NumberType.opt<any>({ label: 'Other (SQ FT)' }): any),
  totalBldgArea: (NumberType.opt<any>({ label: 'Total (SQ FT)' }): any),
  billingContactName: StringType,
  billingContactEmail: EmailType,
  billingContactPhone: (PhoneType.opt<any>({ label: 'Billing Contact Phone' }): any),
  billingAddressStreet: (StringType.opt<any>({ label: 'Street' }): any),
  billingAddressCity: (StringType.opt<any>({ label: 'City' }): any),
  billingAddressState: (StringType.opt<any>({ label: 'State' }): any),
  billingAddressZip: (StringType.opt<any>({ label: 'Zip' }): any),
  billingAddressCountry: (StringType.opt<any>({ label: 'Country' }): any),
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const streetscapeAssetTypeConfig = {
  teaserPhoto: PhotoType,
  address: (AddressType.opt<any>({ label: 'Nearest Address' }): any),
  radiusMeters: (NumberType.opt<any>({ label: 'Radius in meters' }): any),
  category: StringType,
  subcategories: ArrayType,
  tags: TagType,
  notes: NoteType,
  latlon: HiddenType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const streetscapeIssueTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  category: StringType,
  subcategories: ArrayType,
  tags: TagType,
  notes: NoteType,
  details: HTMLType,
  maintenanceDetails: StringType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  dateOfIncident: DateType,
  createdAt: DateType,
  timeOfIncident: StringType,
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const tenantTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  category: StringType,
  subcategories: ArrayType,
  tags: (ArrayType.opt<any>({ hideWhen: isEmbedded }): any),
  description: HTMLType,
  use: StringType,
  rentableSqft: SqFtType,
  businessOpenedOn: DateType,
  businessClosedOn: DateType,
  leaseStart: DateType,
  leaseExpiration: DateType,
  askingPricePerSqft: SqFtType,
  contractValuePerSqft: SqFtType,
  url: (UrlType.opt<any>({ label: 'Website' }): any),
  hours: (rowT(toHours): any),
  phone: PhoneType,
  fax: PhoneType,
  email: EmailType,
  perks: StringType,
  facebookLink: (UrlType.opt<any>({ label: 'Facebook' }): any),
  instagramLink: (UrlType.opt<any>({ label: 'Instagram' }): any),
  twitterLink: (UrlType.opt<any>({ label: 'Twitter' }): any),
  youtubeLink: (UrlType.opt<any>({ label: 'YouTube' }): any),
  pinterestLink: (UrlType.opt<any>({ label: 'Pinterest' }): any),
  yelpLink: (UrlType.opt<any>({ label: 'Yelp' }): any),
  primaryContactName: StringType.opt<any>({ hideWhen: isEmbedded }),
  primaryContactPhone: PhoneType.opt<any>({ hideWhen: isEmbedded }),
  primaryContactEmail: EmailType.opt<any>({ hideWhen: isEmbedded }),
  secondaryContactName: StringType.opt<any>({ hideWhen: isEmbedded }),
  secondaryContactPhone: PhoneType.opt<any>({ hideWhen: isEmbedded }),
  secondaryContactEmail: EmailType.opt<any>({ hideWhen: isEmbedded }),
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

const unitTypeConfig = {
  teaserPhoto: PhotoType,
  address: AddressType,
  latlon: HiddenType,
  use: StringType,
  category: StringType,
  subcategories: ArrayType,
  floor: StringType,
  suite: StringType,
  rentableSqft: SqFtType,
  tags: TagType,
  notes: NoteType,
  annualDistrictPayment: NumberType,
  districtPropertyClass: StringType,
  mapLabel: (StringType.opt<any>({ hideWhen: isEmbedded }): any),
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
  contactName: StringType,
  contactPhone: StringType,
  contactEmail: StringType,
};

const orgTypeConfig = {
  status: StringType,
  address: AddressType,
  latlon: HiddenType,
  category: StringType,
  tags: TagType,
  notes: NoteType,
  updatedAt: (DateType.opt<any>({ label: 'Updated On' }): any),
};

export const resourceConfigs = {
  Building: buildingTypeConfig,
  Contact: contactTypeConfig,
  Development: developmentTypeConfig,
  Event: eventTypeConfig,
  Listing: listingTypeConfig,
  Property: propertyTypeConfig,
  StreetscapeAsset: streetscapeAssetTypeConfig,
  StreetscapeIssue: streetscapeIssueTypeConfig,
  Tenant: tenantTypeConfig,
  Organization: orgTypeConfig,
  Unit: unitTypeConfig,
};

export default function getTypeConfig(
  resourceName: $Keys<typeof resourceConfigs>
): StructType {
  function addSubresource(locals) {
    const { id: resourceId } = locals.value;
    if (!resourceId) return null;
    return <Subresource resourceId={resourceId} resourceName={resourceName} />;
  }
  return ((t
    .struct(resourceConfigs[resourceName])
    .opt<any>({ after: addSubresource }): any): StructType);
}
